import * as React from 'react';
import {
    Checkbox,
    CommandBar,
    DefaultButton,
    DetailsList,
    DetailsListLayoutMode,
    Dialog,
    DialogFooter,
    DialogType,
    Dropdown,
    IDropdownOption,
    IColumn,
    ICommandBarItemProps,
    IconButton,
    Label,
    MessageBar,
    MessageBarType,
    Panel,
    PanelType,
    PrimaryButton,
    Selection,
    SelectionMode,
    Separator,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    TextField,
} from '@fluentui/react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ApiHelper } from '../../../ApiHelper';
import {
    AgentTaskListCollectionResult,
    AgentTaskListDetailResult,
    AgentTaskListSummary,
    PageShell,
    SyntheticBanner,
    formatCompactDate,
    formatGranularity,
    getTaskTypeLabel,
    useCurrentService,
    useDocumentTitle,
} from '../shared';
import { CreateTaskListPanel } from '../panels/CreateTaskListPanel';

export function AgentTaskManagerLandingPage() {
    useDocumentTitle('Agent Task Manager');

    const navigate = useNavigate();
    const { serviceName } = useParams();
    const service = useCurrentService(serviceName);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const [result, setResult] = React.useState<AgentTaskListCollectionResult | null>(null);
    const [isCreatePanelOpen, setCreatePanelOpen] = React.useState(false);

    const loadTaskLists = React.useCallback(() => {
        if (!serviceName) {
            return;
        }

        setLoading(true);
        setError(null);

        ApiHelper(`/api/service/${serviceName}/agenttasklists`)
            .then((response: AgentTaskListCollectionResult) => {
                setResult(response);
                setLoading(false);
            })
            .catch((err: Error) => {
                setError(err.message || 'Failed to load task lists.');
                setResult(null);
                setLoading(false);
            });
    }, [serviceName]);

    React.useEffect(() => {
        loadTaskLists();
    }, [loadTaskLists]);

    const columns = React.useMemo<IColumn[]>(() => [
        {
            key: 'name',
            name: 'Name',
            minWidth: 220,
            isResizable: true,
            onRender: (item: AgentTaskListSummary) => (
                <a
                    href="#"
                    onClick={event => {
                        event.preventDefault();
                        navigate(`/service/${serviceName}/agenttasks/list/${item.taskListId}${item.latestSnapshotId ? `?snapshotId=${item.latestSnapshotId}` : ''}`);
                    }}
                >
                    {item.name}
                </a>
            ),
        },
        {
            key: 'taskType',
            name: 'Task type',
            minWidth: 150,
            onRender: (item: AgentTaskListSummary) => getTaskTypeLabel(item.taskType),
        },
        {
            key: 'process',
            name: 'Process',
            minWidth: 120,
            onRender: (item: AgentTaskListSummary) => item.taskType === 4 ? 'All' : item.processName,
        },
        {
            key: 'generation',
            name: 'Generation',
            minWidth: 90,
            onRender: (item: AgentTaskListSummary) => item.generation,
        },
        {
            key: 'granularity',
            name: 'Granularity',
            minWidth: 110,
            onRender: (item: AgentTaskListSummary) => item.taskType === 4 ? 'N/A' : formatGranularity(item.granularity),
        },
        {
            key: 'version',
            name: 'Version',
            minWidth: 80,
            onRender: (item: AgentTaskListSummary) => item.version,
        },
        {
            key: 'tasks',
            name: 'Tasks',
            minWidth: 70,
            onRender: (item: AgentTaskListSummary) => item.taskCount,
        },
        {
            key: 'snapshots',
            name: 'Snapshots',
            minWidth: 90,
            onRender: (item: AgentTaskListSummary) => item.snapshotCount,
        },
        {
            key: 'latestSnapshotDate',
            name: 'Latest snapshot',
            minWidth: 130,
            onRender: (item: AgentTaskListSummary) => formatCompactDate(item.latestSnapshotDate),
        },
    ], [navigate, serviceName]);

    const commandItems: ICommandBarItemProps[] = [
        {
            key: 'create',
            text: 'Create new task list',
            iconProps: { iconName: 'Add' },
            onClick: () => setCreatePanelOpen(true),
        },
        {
            key: 'refresh',
            text: 'Refresh',
            iconProps: { iconName: 'Refresh' },
            onClick: loadTaskLists,
        },
        {
            key: 'questions',
            text: 'Open questions',
            iconProps: { iconName: 'Help' },
            onClick: () => navigate(`/service/${serviceName}/agenttasks/questions`),
        },
        {
            key: 'changes',
            text: 'Proposed changes',
            iconProps: { iconName: 'BulletedList' },
            onClick: () => navigate(`/service/${serviceName}/agenttasks/changes`),
        },
        {
            key: 'dashboard',
            text: 'Task Dashboard',
            iconProps: { iconName: 'ViewDashboard' },
            onClick: () => navigate(`/service/${serviceName}/agenttasks/dashboard`),
        },
        {
            key: 'autopr',
            text: 'PR Dashboard',
            iconProps: { iconName: 'BranchPullRequest' },
            onClick: () => navigate(`/service/${serviceName}/agenttasks/autopr`),
        },
        {
            key: 'summary',
            text: 'Summary',
            iconProps: { iconName: 'ReportDocument' },
            onClick: () => navigate(`/service/${serviceName}/agenttasks/summary`),
        },
        {
            key: 'definitions',
            text: 'Job definitions',
            iconProps: { iconName: 'Edit' },
            onClick: () => navigate(`/service/${serviceName}/agenttasks/definitions`),
        },
    ];

    return (
        <PageShell>
            <SyntheticBanner visible={!!result?.isSynthetic} />

            <CommandBar items={commandItems} />

            <Text variant="medium">
                Create task lists, browse snapshot-backed tasks, and open the task dashboard,
                PR dashboard, or summary for the selected service.
            </Text>

            {error && (
                <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
            )}

            {loading ? (
                <Spinner size={SpinnerSize.large} label="Loading task lists..." />
            ) : (
                <DetailsList
                    items={result?.taskLists ?? []}
                    columns={columns}
                    selectionMode={SelectionMode.none}
                    layoutMode={DetailsListLayoutMode.justified}
                />
            )}

            {serviceName && (
                <CreateTaskListPanel
                    isOpen={isCreatePanelOpen}
                    serviceName={serviceName}
                    service={service}
                    onDismiss={() => setCreatePanelOpen(false)}
                    onCreated={created => {
                        setCreatePanelOpen(false);
                        navigate(`/service/${serviceName}/agenttasks/list/${created.taskList.taskListId}${created.latestSnapshot?.snapshotId ? `?snapshotId=${created.latestSnapshot.snapshotId}` : ''}`);
                    }}
                />
            )}
        </PageShell>
    );
}
